import { useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { projects } from '../../mocks/projectData';
import ProjectNavbar from './components/ProjectNavbar';
import UploadModal from '../gallery/components/UploadModal';
import Lightbox from '../gallery/components/Lightbox';

interface GalleryImage {
  id: string;
  url: string;
  uploadedAt: Date;
}

export default function ProjectPage() {
  const { slug } = useParams<{ slug: string }>();
  const project = projects.find(p => p.slug === slug);

  const [images, setImages] = useState<GalleryImage[]>([]);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);

  useEffect(() => {
    if (project) {
      const savedImages = localStorage.getItem(`project-${project.slug}-images`);
      if (savedImages) {
        const parsed = JSON.parse(savedImages);
        setImages(parsed.map((img: any) => ({
          ...img,
          uploadedAt: new Date(img.uploadedAt)
        })));
      }
    }
  }, [project]);

  const handleUpload = (files: File[]) => {
    const newImages: GalleryImage[] = files.map(file => ({
      id: `${Date.now()}-${Math.random()}`,
      url: URL.createObjectURL(file),
      uploadedAt: new Date()
    }));

    const updatedImages = [...images, ...newImages];
    setImages(updatedImages);
    
    if (project) {
      localStorage.setItem(`project-${project.slug}-images`, JSON.stringify(updatedImages));
    }
  };

  const handleDelete = (id: string) => {
    const updatedImages = images.filter(img => img.id !== id);
    setImages(updatedImages);
    
    if (project) {
      localStorage.setItem(`project-${project.slug}-images`, JSON.stringify(updatedImages));
    }
  };

  if (!project) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <ProjectNavbar />

      <div className="pt-20">
        <div className="relative w-full h-[60vh] min-h-[500px]">
          <img
            src={project.image}
            alt={project.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
          
          <div className="absolute bottom-0 left-0 right-0 p-12">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-end justify-between">
                <div>
                  <h1 className="text-5xl lg:text-6xl font-bold text-white mb-4">
                    {project.name}
                  </h1>
                  <div className="flex items-center gap-6 text-white/90">
                    <div className="flex items-center gap-2">
                      <i className="ri-map-pin-line text-xl"></i>
                      <span className="text-lg">{project.location}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="ri-calendar-line text-xl"></i>
                      <span className="text-lg">{project.year}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="ri-ruler-line text-xl"></i>
                      <span className="text-lg">{project.size}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
          <div className="mb-12">
            <p className="text-lg text-gray-600 leading-relaxed">
              {project.description}
            </p>
          </div>

          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-bold text-gray-900">프로젝트 갤러리</h2>
            <button
              onClick={() => setIsUploadModalOpen(true)}
              className="flex items-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-upload-2-line text-lg"></i>
              <span className="font-medium">사진 업로드</span>
            </button>
          </div>

          {images.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 bg-white rounded-xl border-2 border-dashed border-gray-200">
              <div className="w-20 h-20 flex items-center justify-center bg-gray-100 rounded-full mb-4">
                <i className="ri-image-line text-4xl text-gray-400"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                아직 업로드된 사진이 없습니다
              </h3>
              <p className="text-gray-500 mb-6">
                프로젝트 사진을 업로드하여 갤러리를 채워보세요
              </p>
              <button
                onClick={() => setIsUploadModalOpen(true)}
                className="px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                첫 사진 업로드하기
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {images.map((image, index) => (
                <div
                  key={image.id}
                  className="relative group aspect-square rounded-lg overflow-hidden bg-gray-100 cursor-pointer"
                  onClick={() => setSelectedImageIndex(index)}
                >
                  <img
                    src={image.url}
                    alt={`Gallery ${index + 1}`}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300"></div>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(image.id);
                    }}
                    className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-white/90 hover:bg-red-500 text-gray-700 hover:text-white rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 cursor-pointer"
                  >
                    <i className="ri-delete-bin-line text-base"></i>
                  </button>

                  <div className="absolute bottom-3 left-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-10 h-10 flex items-center justify-center bg-white/90 rounded-full">
                      <i className="ri-zoom-in-line text-xl text-gray-900"></i>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <UploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUpload={handleUpload}
      />

      {selectedImageIndex !== null && (
        <Lightbox
          images={images.map(img => img.url)}
          currentIndex={selectedImageIndex}
          onClose={() => setSelectedImageIndex(null)}
          onNavigate={setSelectedImageIndex}
        />
      )}
    </div>
  );
}
